<template>
  <div class="postit-board-container">
    <div class="board-header">
      <h2 class="board-title">
        <a-icon type="pushpin" />
        {{ $t("message.postitBoardTitle") }}
      </h2>
      <div class="header-actions">
        <a-button 
          type="primary" 
          @click="addNewNote"
          class="add-note-btn"
        >
          <a-icon type="plus" />
          {{ $t("message.postitNewNoteButton") }}
        </a-button>
        <a-button 
          type="default" 
          @click="closeBoardOverlay"
          class="close-btn"
        >
          <a-icon type="close" />
        </a-button>
      </div>
    </div>
    
    <div 
      class="cork-board"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dblclick="addNoteAtPosition"
    >
      <!-- Mantar pano deseni -->
      <div class="cork-pattern"></div>
      
      <!-- Post-it notları -->
      <postit-note
        v-for="note in notes"
        :key="note.id"
        :note="note"
        :style="{ 
          left: note.x + 'px', 
          top: note.y + 'px',
          transform: `rotate(${note.rotation}deg)`
        }"
        @edit="editNote"
        @delete="deleteNote"
        @drag-start="handleNoteDragStart"
        @drag-end="handleNoteDragEnd"
      />
      
      <!-- Pin iğneleri -->
      <div 
        v-for="pin in pins" 
        :key="pin.id"
        class="pin"
        :style="{ left: pin.x + 'px', top: pin.y + 'px' }"
      ></div>
    </div>

    <!-- Not düzenleme sidebar'ı -->
    <div 
      v-if="editorVisible" 
      class="postit-editor-sidebar"
      @click.stop
    >
      <div class="editor-header">
        <h3>{{ editorSidebarTitle }}</h3>
        <a-button type="text" @click="closeEditor" class="close-editor-btn">
          <a-icon type="close" />
        </a-button>
      </div>
      
      <div class="editor-content">
        <!-- Başlık -->
        <div class="form-group">
          <label>{{ $t("message.postitTitleLabel") }}</label>
          <a-input 
            v-model="editorData.title" 
            :placeholder="$t('message.postitTitlePlaceholder')"
            class="editor-input"
          />
        </div>
        
        <!-- İçerik -->
        <div class="form-group">
          <label>{{ $t("message.postitContentLabel") }}</label>
          <a-textarea
            v-model="editorData.text"
            :placeholder="$t('message.postitContentPlaceholder')"
            :rows="8"
            class="editor-textarea"
          />
        </div>

        <!-- Renk seçimi -->
        <div class="form-group">
          <label>{{ $t("message.postitColorLabel") }}</label>
          <div class="color-picker">
            <div 
              v-for="color in postitColors"
              :key="color.name"
              class="color-option"
              :class="{ active: editorData.color === color.name }"
              :style="{ background: color.preview }"
              @click="editorData.color = color.name"
              :title="color.label"
            >
              <a-icon 
                v-if="editorData.color === color.name" 
                type="check" 
                class="check-icon"
              />
            </div>
          </div>
        </div>

        <!-- Boyut ayarları -->
        <div class="form-group">
          <label>{{ $t("message.postitSizeLabel") }}</label>
          <div class="size-controls">
            <div class="size-row">
              <span>{{ $t("message.postitWidthPx", { w: editorData.width }) }}</span>
              <a-slider
                v-model="editorData.width"
                :min="150"
                :max="300"
              />
            </div>
            <div class="size-row">
              <span>{{ $t("message.postitHeightPx", { h: editorData.height }) }}</span>
              <a-slider
                v-model="editorData.height"
                :min="150"
                :max="300"
              />
            </div>
          </div>
        </div>

        <!-- Açı -->
        <div class="form-group">
          <label>{{ $t("message.postitAngleWithDeg", { deg: editorData.rotation }) }}</label>
          <a-slider
            v-model="editorData.rotation"
            :min="-15"
            :max="15"
            :marks="angleSliderMarks"
          />
        </div>
      </div>
      
      <div class="editor-footer">
        <a-button @click="closeEditor">
          {{ $t("message.cancel") }}
        </a-button>
        <a-button 
          type="primary" 
          @click="saveNote"
          :disabled="!editorData.title.trim()"
        >
          <a-icon type="save" />
          {{ editorSaveButtonLabel }}
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import PostitNote from './PostitNote.vue'

export default {
  name: 'PostitBoard',
  components: {
    PostitNote
  },
  data() {
    return {
      notes: [],
      pins: [
        { id: 1, x: 120, y: 50 },
        { id: 2, x: 350, y: 130 },
        { id: 3, x: 570, y: 90 },
        { id: 4, x: 200, y: 300 },
        { id: 5, x: 450, y: 350 },
        { id: 6, x: 80, y: 250 },
        { id: 7, x: 780, y: 180 },
        { id: 8, x: 650, y: 320 },
        { id: 9, x: 420, y: 80 },
        { id: 10, x: 180, y: 480 },
        { id: 11, x: 520, y: 450 },
        { id: 12, x: 720, y: 250 },
        { id: 13, x: 300, y: 380 },
        { id: 14, x: 600, y: 140 },
        { id: 15, x: 380, y: 280 },
        { id: 16, x: 150, y: 180 },
        { id: 17, x: 680, y: 380 },
        { id: 18, x: 480, y: 220 },
        { id: 19, x: 320, y: 480 },
        { id: 20, x: 620, y: 80 }
      ],
      editorVisible: false,
      selectedNote: null,
      draggedNote: null,
      editorData: {
        title: '',
        text: '',
        color: 'yellow',
        width: 200,
        height: 200,
        rotation: 0
      },
    }
  },
  computed: {
    postitColors() {
      return [
        { name: 'yellow', label: this.$t('message.postitColorYellow'), preview: '#ffeb3b' },
        { name: 'pink', label: this.$t('message.postitColorPink'), preview: '#ff4081' },
        { name: 'blue', label: this.$t('message.postitColorBlue'), preview: '#2196f3' },
        { name: 'green', label: this.$t('message.postitColorGreen'), preview: '#4caf50' },
        { name: 'orange', label: this.$t('message.postitColorOrange'), preview: '#ff9800' },
        { name: 'purple', label: this.$t('message.postitColorPurple'), preview: '#9c27b0' }
      ];
    },
    angleSliderMarks() {
      return { 0: this.$t('message.postitAngleMark0') };
    },
    isEditingExistingNote() {
      var sid = this.selectedNote && this.selectedNote.id;
      if (!sid) {
        return false;
      }
      return this.notes.some(function (n) { return n.id === sid; });
    },
    editorSidebarTitle() {
      return this.isEditingExistingNote ? this.$t('message.postitEditNoteHeader') : this.$t('message.postitNewNoteHeader');
    },
    editorSaveButtonLabel() {
      return this.isEditingExistingNote ? this.$t('message.update') : this.$t('message.save');
    }
  },
  mounted() {
    this.loadNotesFromStorage()
  },
  methods: {
    loadNotesFromStorage() {
      const saved = localStorage.getItem('postit-board-notes')
      if (saved) {
        try {
          this.notes = JSON.parse(saved)
        } catch (e) {
          console.error('LocalStorage veri yükleme hatası:', e)
          this.createDefaultNotes()
        }
      } else {
        this.createDefaultNotes()
      }
    },
    
    saveNotesToStorage() {
      try {
        localStorage.setItem('postit-board-notes', JSON.stringify(this.notes))
      } catch (e) {
        console.error('LocalStorage kaydetme hatası:', e)
      }
    },
    
    createDefaultNotes() {
      this.notes = [
        {
          id: 1,
          title: this.$t('message.postitSampleNote1Title'),
          text: this.$t('message.postitSampleNote1Body'),
          color: 'yellow',
          x: 50,
          y: 80,
          rotation: -2,
          width: 200,
          height: 200,
          createdAt: new Date(),
          updatedAt: new Date()
        },
        {
          id: 2,
          title: this.$t('message.postitSampleNote2Title'),
          text: this.$t('message.postitSampleNote2Body'),
          color: 'pink',
          x: 280,
          y: 160,
          rotation: 3,
          width: 180,
          height: 180,
          createdAt: new Date(),
          updatedAt: new Date()
        },
        {
          id: 3,
          title: this.$t('message.postitSampleNote3Title'),
          text: this.$t('message.postitSampleNote3Body'),
          color: 'blue',
          x: 500,
          y: 120,
          rotation: -1,
          width: 200,
          height: 160,
          createdAt: new Date(),
          updatedAt: new Date()
        }
      ]
      this.saveNotesToStorage()
    },

    addNewNote() {
      // Rastgele pozisyon oluştur
      const x = Math.random() * (800 - 200) + 50
      const y = Math.random() * (500 - 200) + 80
      const rotation = (Math.random() - 0.5) * 10
      
      this.selectedNote = {
        id: null, // Yeni not için null
        title: '',
        text: '',
        color: this.getRandomColor(),
        x: x,
        y: y,
        rotation: rotation,
        width: 200,
        height: 200,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      
      this.initializeEditor()
      this.editorVisible = true
    },
    
    addNoteAtPosition(event) {
      // Sadece boş alana çift tıklandığında yeni not ekle
      if (event.target.classList.contains('cork-board') || 
          event.target.classList.contains('cork-pattern')) {
        const rect = this.$el.querySelector('.cork-board').getBoundingClientRect()
        const x = event.clientX - rect.left - 100
        const y = event.clientY - rect.top - 100
        
        this.selectedNote = {
          id: null,
          title: '',
          text: '',
          color: this.getRandomColor(),
          x: Math.max(0, x),
          y: Math.max(0, y),
          rotation: (Math.random() - 0.5) * 10,
          width: 200,
          height: 200,
          createdAt: new Date(),
          updatedAt: new Date()
        }
        
        this.initializeEditor()
        this.editorVisible = true
      }
    },
    
    editNote(note) {
      this.selectedNote = Object.assign({}, note)
      this.initializeEditor()
      this.editorVisible = true
    },
    
    initializeEditor() {
      this.editorData = {
        title: this.selectedNote.title || '',
        text: this.selectedNote.text || '',
        color: this.selectedNote.color || 'yellow',
        width: this.selectedNote.width || 200,
        height: this.selectedNote.height || 200,
        rotation: this.selectedNote.rotation || 0
      }
    },
    
    saveNote() {
      if (!this.editorData.title.trim()) {
        this.$message.warning('Lütfen not başlığını girin')
        return
      }
      
      const noteData = {
        title: this.editorData.title.trim(),
        text: this.editorData.text.trim(),
        color: this.editorData.color,
        width: this.editorData.width,
        height: this.editorData.height,
        rotation: this.editorData.rotation,
        updatedAt: new Date()
      }
      
      if (this.selectedNote.id && this.notes.find(n => n.id === this.selectedNote.id)) {
        // Mevcut notu güncelle
        const index = this.notes.findIndex(n => n.id === this.selectedNote.id)
        this.notes.splice(index, 1, Object.assign({}, this.selectedNote, noteData))
      } else {
        // Yeni not ekle
        const newNote = Object.assign({}, this.selectedNote, noteData, {
          id: Date.now(),
          createdAt: new Date()
        })
        this.notes.push(newNote)
      }
      
      this.saveNotesToStorage()
      this.closeEditor()
    },
    
    deleteNote(noteId) {
      const index = this.notes.findIndex(n => n.id === noteId)
      if (index > -1) {
        this.notes.splice(index, 1)
        this.saveNotesToStorage()
      }
    },
    
    closeEditor() {
      this.editorVisible = false
      this.selectedNote = null
      this.editorData = {
        title: '',
        text: '',
        color: 'yellow',
        width: 200,
        height: 200,
        rotation: 0
      }
    },
    
    closeBoardOverlay() {
      this.$emit('close')
    },
    
    getRandomColor() {
      const colors = ['yellow', 'pink', 'blue', 'green', 'orange', 'purple']
      return colors[Math.floor(Math.random() * colors.length)]
    },
    
    handleNoteDragStart(note) {
      this.draggedNote = note
    },
    
    handleNoteDragEnd() {
      this.draggedNote = null
    },
    
    handleDragOver(event) {
      event.preventDefault()
    },
    
    handleDrop(event) {
      event.preventDefault()
      if (this.draggedNote) {
        const rect = event.currentTarget.getBoundingClientRect()
        const x = event.clientX - rect.left - 100
        const y = event.clientY - rect.top - 100
        
        // Notu yeni pozisyona taşı
        const noteIndex = this.notes.findIndex(n => n.id === this.draggedNote.id)
        if (noteIndex > -1) {
          this.notes[noteIndex].x = Math.max(0, x)
          this.notes[noteIndex].y = Math.max(0, y)
          this.notes[noteIndex].updatedAt = new Date()
          this.saveNotesToStorage() // Pozisyon değişikliğini kaydet
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.postit-board-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(2px);
  z-index: 9999;
  overflow: hidden;
}

.board-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 30px;
  //background: white;
  //border-bottom: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgb(62 56 56 / 10%);
  
  .board-title {
    margin: 0;
    color: #fff;
    font-size: 24px;
    font-weight: 600;
    
    .anticon {
      margin-right: 8px;
      color: #fff;
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  
  .add-note-btn {
    background: #52c41a;
    border-color: #52c41a;
    box-shadow: 0 2px 4px rgba(82, 196, 26, 0.3);
    
    &:hover {
      background: #73d13d;
      border-color: #73d13d;
    }
  }
  
  .close-btn {
    background: #ff4d4f;
    border-color: #ff4d4f;
    color: white;
    box-shadow: 0 2px 4px rgba(255, 77, 79, 0.3);
    
    &:hover {
      background: #ff7875;
      border-color: #ff7875;
      color: white;
    }
  }
}

.cork-board {
  position: relative;
  width: 100%;
  height: calc(100vh - 80px);
  //background-image: url('https://static.supsis.live/common/image/corkboard.jpg');
  //background-size: cover;
  //background-position: center;
  //background-repeat: no-repeat;
  cursor: crosshair;
  overflow: hidden;
  
  // Resmin üzerine hafif overlay ekleyerek post-it'lerin daha iyi görünmesini sağla
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    pointer-events: none;
  }
  
  // Hafif dokulu overlay ekle
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(0, 0, 0, 0.02) 2px,
        rgba(0, 0, 0, 0.02) 4px
      ),
      repeating-linear-gradient(
        90deg,
        transparent,
        transparent 2px,
        rgba(0, 0, 0, 0.02) 2px,
        rgba(0, 0, 0, 0.02) 4px
      );
    pointer-events: none;
  }
}

.cork-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.3;
  background-image:
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(139, 69, 19, 0.1) 2px,
      rgba(139, 69, 19, 0.1) 4px
    ),
    repeating-linear-gradient(
      90deg,
      transparent,
      transparent 2px,
      rgba(139, 69, 19, 0.1) 2px,
      rgba(139, 69, 19, 0.1) 4px
    );
  pointer-events: none;
}

.pin {
  position: absolute;
  width: 12px;
  height: 12px;
  background: radial-gradient(circle, #ff6b6b 30%, #e55555 70%);
  border-radius: 50%;
  box-shadow: 
    0 2px 4px rgba(0, 0, 0, 0.3),
    inset 0 1px 2px rgba(255, 255, 255, 0.3);
  z-index: 10;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: radial-gradient(circle, transparent 60%, rgba(0, 0, 0, 0.1) 100%);
  }
}

// Editor Sidebar Stilleri
.postit-editor-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: white;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  
  .editor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e8e8e8;
    background: #fafafa;
    
    h3 {
      margin: 0;
      color: #333;
      font-size: 16px;
      font-weight: 600;
    }
    
    .close-editor-btn {
      color: #666;
      
      &:hover {
        color: #ff4d4f;
      }
    }
  }
  
  .editor-content {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    
    .form-group {
      margin-bottom: 20px;
      
      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
        font-size: 14px;
      }
    }
    
    .editor-input, .editor-textarea {
      width: 100%;
      border-radius: 6px;
      border: 1px solid #d9d9d9;
      
      &:focus {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
      }
    }
    
    .color-picker {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      
      .color-option {
        width: 36px;
        height: 36px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 2px solid transparent;
        transition: all 0.2s ease;
        
        &:hover {
          transform: scale(1.1);
        }
        
        &.active {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        }
        
        .check-icon {
          color: white;
          font-size: 14px;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
        }
      }
    }
    
    .size-controls {
      .size-row {
        margin-bottom: 12px;
        
        span {
          display: block;
          margin-bottom: 8px;
          color: #666;
          font-size: 13px;
        }
      }
    }
  }
  
  .editor-footer {
    padding: 16px 20px;
    border-top: 1px solid #e8e8e8;
    background: #fafafa;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }
}

// Mobil responsive
@media (max-width: 768px) {
  .postit-editor-sidebar {
    width: 100vw;
    right: 0;
  }
}
</style>
